import { Box, Skeleton, useTheme } from "@mui/material";
import { CustomLayerProps } from "@nivo/line";
import { ResponsiveLine } from "@common/components/nivoCharts/NivoCharts";
import { useTranslation } from "@localization/useTranslation";
import { CountAboveAppetitePoint } from "../RiskManagementReport.types";
import { buildCountAxisTicks } from "../RiskManagementReport.utils";

const lineColor = "#F7447A";
const appetiteLimitColor = "#B0A8B8";
const gridColor = "#EDECF3";

interface CountAboveAppetiteChartProps {
    isLoading: boolean;
    points: Array<CountAboveAppetitePoint>;
    appetiteLimit: number;
}

// Drawn as a custom layer rather than a nivo marker: the marker component declares its legend props as
// required in propTypes while defaulting them internally, which spams prop-type warnings.
function appetiteLimitLine(appetiteLimit: number) {
    return function AppetiteLimitLine({ innerWidth, yScale }: CustomLayerProps) {
        const y = (yScale as (value: number) => number)(appetiteLimit);
        return <line x1={0} x2={innerWidth} y1={y} y2={y} stroke={appetiteLimitColor} strokeWidth={2} strokeDasharray="6 6" />;
    };
}

function CountAboveAppetiteChart({ isLoading, points, appetiteLimit }: CountAboveAppetiteChartProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();

    if (isLoading) {
        return <Skeleton variant="rounded" width="100%" height="100%" />;
    }

    const highestPoint = Math.max(1, appetiteLimit, ...points.map((point) => point.y));
    const { axisMax, tickValues } = buildCountAxisTicks(highestPoint);

    const legendLabelSx = { color: theme.palette.primary.dark, fontSize: theme.typography.pxToRem(11) };

    return (
        <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ width: "100%", height: 0, flex: "1 1 auto", minHeight: 0, "& rect[data-ref='mesh-interceptor']": { cursor: "pointer !important" } }}>
                <ResponsiveLine
                    data={[{ id: "aboveAppetite", data: points }]}
                    margin={{ top: 10, right: 20, bottom: 30, left: 40 }}
                    xScale={{ type: "point" }}
                    yScale={{ type: "linear", min: 0, max: axisMax }}
                    curve="monotoneX"
                    colors={[lineColor]}
                    lineWidth={3}
                    enableArea
                    areaOpacity={1}
                    defs={[
                        {
                            id: "aboveAppetiteGradient",
                            type: "linearGradient",
                            colors: [
                                { offset: 0, color: lineColor, opacity: 0.25 },
                                { offset: 100, color: lineColor, opacity: 0 },
                            ],
                        },
                    ]}
                    fill={[{ match: "*", id: "aboveAppetiteGradient" }]}
                    pointSize={9}
                    pointColor={lineColor}
                    pointBorderWidth={0}
                    enableGridX={false}
                    gridYValues={tickValues}
                    axisLeft={{ tickSize: 0, tickPadding: 10, tickValues }}
                    axisBottom={{ tickSize: 0, tickPadding: 10 }}
                    isInteractive
                    useMesh
                    tooltip={({ point }) => (
                        <Box
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${gridColor}`,
                                borderRadius: "4px",
                                boxShadow: theme.shadows[2],
                                color: theme.palette.primary.dark,
                                padding: "4px 8px",
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                        >
                            {point.data.y as number}
                        </Box>
                    )}
                    layers={["grid", "axes", "areas", appetiteLimitLine(appetiteLimit), "lines", "points", "mesh"]}
                    theme={{
                        text: { fill: theme.palette.primary.dark, fontSize: 13 },
                        grid: { line: { stroke: gridColor } },
                    }}
                />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: "4px", backgroundColor: lineColor }} />
                    <Box sx={legendLabelSx}>{translateString("riskManagementReportAboveAppetite")}</Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 20, borderTop: `3px dashed ${appetiteLimitColor}` }} />
                    <Box sx={legendLabelSx}>{translateString("riskManagementReportAppetiteLimitLegend", { limit: appetiteLimit })}</Box>
                </Box>
            </Box>
        </Box>
    );
}

export default CountAboveAppetiteChart;
